import { useAtomValue, useSetAtom } from 'jotai';

import { aggregationsAtom } from '@core/Atoms/Search/AggregationsAtom';
import { ukLocale } from '@core/Utils/Intl/MappedLocales';
import Button from '@gds/Button/Button';
import { Location as LocationIcon } from '@gds/Icons/Paths/Location';
import { Input } from '@gds/Input/Input';
import Slider from '@gds/Slider/Slider';
import { Typography } from '@gds/Typography/Typography';
import { openQuickFilterAtom } from '@plp/Atoms/QuickFilterOpenAtom';

import ApplyButton from '../ApplyButton/ApplyButton';
import type { LocationFilterProps } from './LocationFilter.entity';
import useLocationFilterUk from './UseLocationFilterUk';

import styles from './LocationFilter.module.css';

export const LocationFilterUk = ({ strings, applyButton }: LocationFilterProps) => {
  const {
    handleInputBlur,
    handleInputChange,
    canReset,
    postcode,
    resetAllFilter,
    sliderProps,
    error,
    handleApply,
    localSliderDisplayValue,
    isValid,
    shouldHideRangeFilters,
  } = useLocationFilterUk(strings.ukErrorMessage, applyButton);

  const { totalCount } = useAtomValue(aggregationsAtom);

  const setOpenQuickFilter = useSetAtom(openQuickFilterAtom);

  const handleLocationApply = () => {
    handleApply();
    setOpenQuickFilter(null);
  };

  return (
    <section className={styles.locationFilter}>
      <div className={styles.inputs}>
        <Input
          id="filter-panel-postcode-input"
          dataTestId="filter-panel-postcode-input"
          className={styles.postcode}
          error={error}
          onBlur={() => handleInputBlur()}
          onChange={e => handleInputChange(e)}
          placeholder={strings.postcode}
          required
          rightIcon={<LocationIcon />}
          value={postcode}
          labelPlacement="above"
        />
      </div>

      {!shouldHideRangeFilters && (
        <>
          <Slider
            {...sliderProps}
            selectedRangeIndexes={
              sliderProps.selectedRangeIndexes.length === 2
                ? [sliderProps.selectedRangeIndexes[0], sliderProps.selectedRangeIndexes[1]]
                : sliderProps.selectedRangeIndexes.length === 1
                  ? [sliderProps.selectedRangeIndexes[0]]
                  : undefined
            }
          />
          <div className={styles.auxInfo}>
            <div>
              <Typography Component="span" variant="subheading3">
                {!applyButton
                  ? strings.distance
                  : localSliderDisplayValue === 100
                    ? strings.distance
                    : `${strings.distanceLabel}: ${localSliderDisplayValue} miles`}
              </Typography>
              <Typography Component="span" className={styles.resultCount} variant="caption2">
                ({totalCount.toLocaleString(ukLocale)})
              </Typography>
            </div>
            {!applyButton && (
              <Button
                color="secondary"
                disabled={!canReset()}
                onClick={resetAllFilter}
                size="small"
                variant="link"
              >
                {strings.reset}
              </Button>
            )}
          </div>
        </>
      )}
      {applyButton && (
        <ApplyButton
          handleApply={handleLocationApply}
          ctaText={strings.apply}
          disabled={!isValid}
        />
      )}
    </section>
  );
};
